/**
 * How the parts meet, as functions main.ts calls and tests can drive
 * without a page: every link that opens (whoever opened it) feeds the
 * reconciler and names its peers for the Fx; a presence frame goes on the
 * socket of the World it is about; a call this tab did not send by hand
 * (the Director's on the server, a scenario's) still plays its beats; a
 * fix-it chosen on a refusal's sentence runs, returning a scrubbed World to
 * Live first.
 */

import type {
  Actions, CallRecord, Catalogue, Cue, Fx, Link, Links, Origin, PresenceIn, PresenceOut, Reconciler, Source, Unsub, WorldKey, FixIt,
} from '../contracts';
import { beatSourceOf, runFixit, verdictCues, type VerdictLook } from './actions';

/** What a link's frames and events mean to the rest of the app. */
export interface LinkListeners {
  readonly reconciler: Pick<Reconciler, 'nudge'>;
  readonly fx: Pick<Fx, 'setPeer' | 'forgetPeer'> & { readonly conductor: Pick<Fx['conductor'], 'play'> };
  /** Something about the pool changed (a link opened, or changed state): the HUD redraws. */
  changed(): void;
  /** A link went live: frames sent before it was (a `focus` on opening a World) can be said now. */
  live?(world: WorldKey): void;
}

/** A link's frames, as the app hears them: peers named, peers forgotten, peer frames played on their World. */
export const hearPresence = (world: WorldKey, f: PresenceOut, to: LinkListeners['fx']): void => {
  switch (f.type) {
    case 'presence.roster':
      for (const p of f.peers) to.setPeer(p.sid, { name: p.name, color: p.color });
      return;
    case 'presence.joined':
      to.setPeer(f.peer.sid, { name: f.peer.name, color: f.peer.color });
      return;
    case 'presence.left':
      to.forgetPeer(f.sid);
      return;
    case 'presence':
      to.conductor.play({ kind: 'peer', sid: f.sid, world, frame: f.frame });
      return;
    case 'presence.full':
      return;
  }
};

/**
 * The pool, as every screen sees it: the same links, and each one, the
 * first time anyone opens or finds it, feeds the reconciler (events and
 * hellos, polled heads included) and plays its peers.
 */
export const attachingLinks = (links: Links, to: LinkListeners): Links => {
  const attached = new WeakSet<Link>();
  const attach = (world: WorldKey, link: Link): Link => {
    if (attached.has(link)) return link;
    attached.add(link);
    link.onEvent((event) => to.reconciler.nudge(world, { kind: 'event', event }));
    link.onHello((d) => to.reconciler.nudge(world, { kind: 'hello', seq: d.seq, head: d.head, describe: d }));
    link.onPresence((f) => hearPresence(world, f, to.fx));
    link.onState((state) => {
      to.changed();
      if (state === 'live') to.live?.(world);
    });
    to.changed();
    return link;
  };
  return {
    open: (world, opts) => attach(world, links.open(world, opts)),
    get(world) {
      const link = links.get(world);
      return link ? attach(world, link) : undefined;
    },
    worlds: () => links.worlds(),
    closeAll() {
      links.closeAll();
      to.changed();
    },
  };
};

/** A frame goes on the socket of the World it is about, else the World in focus; never from a hidden tab. */
export const sendPresence = (links: Pick<Links, 'get'>, frame: PresenceIn, world: WorldKey | undefined, focus: WorldKey | undefined, hidden: boolean): boolean => {
  if (hidden) return false;
  const w = world ?? focus;
  if (!w) return false;
  return links.get(w)?.send(frame) ?? false;
};

/** The beat a call belongs to when this tab's hand did not send it: the Director's call joins its forming intent, a scenario step its ghost. */
export const beatIdOf = (source: Source): string | null =>
  source.kind === 'director' ? `director:${source.callId}` : source.kind === 'scenario' ? `scenario:${source.run}:${source.step}` : null;

/** Flight and door when such a call is sent; its verdict when it answers. Hand calls play their own through Actions. */
export const remoteBeats = (record: CallRecord, phase: 'sent' | 'answered', catalogue: Catalogue, look: VerdictLook = {}): Cue[] => {
  const id = beatIdOf(record.source);
  if (!id) return [];
  if (phase === 'sent') {
    const source = beatSourceOf(record.source, null);
    return [{ kind: 'flight', id, op: record.op, source, target: null, cls: catalogue.byId(record.op)?.cls ?? 'write' }, { kind: 'door', id }];
  }
  return verdictCues(id, record.op, record, look);
};

/** Play every call's beats that Actions does not: listen once, on the Client. */
export const playRemoteBeats = (client: { on(fn: (r: CallRecord, p: 'sent' | 'answered') => void): Unsub }, fx: Pick<Fx, 'conductor'>, catalogue: () => Catalogue): Unsub =>
  client.on((record, phase) => { for (const cue of remoteBeats(record, phase, catalogue())) fx.conductor.play(cue); });

/** A fix-it chosen on a refusal's sentence: a World scrubbed back returns to Live first (§5.5 rule 9), then it runs, or opens as a draft. */
export const takeFixit = (
  actions: Actions, catalogue: Catalogue, fix: FixIt, at: Origin, via: 'click' | 'keyboard',
  scrub: { seq(world: string): number | null; live(world: string): void },
): void => {
  const w = typeof fix.args.w === 'string' ? fix.args.w : null;
  if (w && scrub.seq(w) !== null) scrub.live(w);
  runFixit(actions, catalogue, fix, at, via);
};
